import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import pharmacyService from '../../../services/pharmacyService';
import { usePharmacy } from '../pharmacyContext';
import { CENTRAL_PHARMACY_PATHS } from '../pharmacyPaths';
import { PHARMACY_COPY } from '../pharmacyCopy';
import {
  alertTypeLabel,
  alertTypePillClass,
  alertDetailText
} from '../components/alertsUi';
import Icon from '../../common/Icons';
import { PharmacyPageHeader, LoadingBlock, EmptyBlock, formatNumber } from '../ui';

const copy = PHARMACY_COPY.DASHBOARD;

function healthTone(alertSummary) {
  const critical =
    Number(alertSummary?.out_of_stock || 0) + Number(alertSummary?.expired || 0);
  const warn =
    Number(alertSummary?.low_stock || 0) + Number(alertSummary?.expiring || 0);
  if (critical > 0) return { key: 'critical', label: copy.HEALTH_CRITICAL };
  if (warn > 0) return { key: 'watch', label: copy.HEALTH_WATCH };
  return { key: 'ok', label: copy.HEALTH_OK };
}

function QueueItem({ label, hint, value, to, tone = 'default', icon }) {
  const navigate = useNavigate();
  const hot = Number(value) > 0 && tone !== 'success';
  return (
    <button
      type="button"
      className={`cp-dash-queue__item${hot ? ' is-hot' : ''}${
        tone === 'success' ? ' is-success' : ''
      }${tone === 'danger' ? ' is-danger' : ''}`}
      onClick={() => navigate(to)}
    >
      <span className="cp-dash-queue__icon" aria-hidden>
        <Icon name={icon} size={16} />
      </span>
      <span className="cp-dash-queue__body">
        <span className="cp-dash-queue__label">{label}</span>
        {hint ? <span className="cp-dash-queue__hint">{hint}</span> : null}
      </span>
      <span className="cp-dash-queue__value">{formatNumber(value)}</span>
      <span className="cp-dash-queue__chevron" aria-hidden>
        <Icon name="chevron-right" size={14} />
      </span>
    </button>
  );
}

export default function PharmacyDashboardPage() {
  const { reloadNonce, toast } = usePharmacy();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const dash = await pharmacyService.dashboard();
      if (dash.success) setData(dash.data);
      else {
        setData(null);
        toast?.('danger', dash.message || 'Tableau de bord indisponible.');
      }
    } catch (e) {
      setData(null);
      toast?.('danger', e.message || 'Tableau de bord indisponible.');
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    load();
  }, [load, reloadNonce]);

  const health = useMemo(() => healthTone(data?.alerts), [data]);

  if (loading) return <LoadingBlock label="Chargement du tableau de bord…" />;
  if (!data) return <EmptyBlock label="Tableau de bord indisponible." />;

  const { stock, alerts: alertSummary, workflow, recent_alerts: recentAlerts = [] } = data;
  const attention = Number(data.attention_count || 0);
  const stockAlerts =
    Number(alertSummary?.out_of_stock || 0) + Number(alertSummary?.low_stock || 0);
  const expiryAlerts =
    Number(alertSummary?.expired || 0) + Number(alertSummary?.expiring || 0);

  return (
    <div className="cp-dashboard cp-dashboard--v2">
      <PharmacyPageHeader
        badge={copy.BADGE}
        title={copy.TITLE}
        description={
          data.central_site
            ? copy.DESC_WITH_SITE.replace('{name}', data.central_site.name)
            : copy.DESC_NO_SITE
        }
        actions={
          <>
            <button
              type="button"
              className="cp-icon-btn"
              onClick={load}
              aria-label={copy.REFRESH_ARIA}
              title={copy.REFRESH_ARIA}
            >
              <Icon name="refresh" size={16} />
            </button>
            <button
              type="button"
              className="cp-btn cp-btn--ghost cp-btn--sm"
              onClick={() => navigate(CENTRAL_PHARMACY_PATHS.ORDERS)}
            >
              {copy.CTA_ORDERS}
            </button>
            <button
              type="button"
              className="cp-btn cp-btn--primary cp-btn--sm"
              onClick={() => navigate(CENTRAL_PHARMACY_PATHS.DISPENSATION_NEW)}
            >
              <Icon name="plus" size={14} aria-hidden />
              {copy.CTA_DISPENSE}
            </button>
          </>
        }
      />

      <div className={`cp-dash-attention cp-dash-attention--${health.key}`}>
        <div className="cp-dash-attention__main">
          <span className={`cp-status-pill cp-dash-attention__pill cp-status-pill--${
            health.key === 'ok' ? 'ok' : health.key === 'watch' ? 'warn' : 'danger'
          }`}>
            {health.label}
          </span>
          <div>
            <strong className="cp-dash-attention__title">{copy.ATTENTION_TITLE}</strong>
            <p className="cp-dash-attention__hint">
              {attention > 0
                ? copy.ATTENTION_HINT.replace('{n}', formatNumber(attention))
                : copy.ATTENTION_OK}
            </p>
          </div>
        </div>
        {data.central_site ? (
          <span className="cp-dash-attention__site">{data.central_site.name}</span>
        ) : null}
      </div>

      <section className="cp-catalog-stats cp-catalog-stats--dash" aria-label="Indicateurs clés">
        <article className="cp-catalog-stat cp-catalog-stat--ok">
          <span className="cp-catalog-stat__label">{copy.KPI_UNITS}</span>
          <span className="cp-catalog-stat__value">{formatNumber(stock.total_units)}</span>
        </article>
        <article className="cp-catalog-stat">
          <span className="cp-catalog-stat__label">{copy.KPI_VALUE}</span>
          <span className="cp-catalog-stat__value">{formatNumber(stock.total_value)} Ar</span>
          <span className="cp-catalog-stat__hint">{copy.KPI_VALUE_HINT}</span>
        </article>
        <article className="cp-catalog-stat">
          <span className="cp-catalog-stat__label">{copy.KPI_MEDS}</span>
          <span className="cp-catalog-stat__value">{formatNumber(stock.active_medications)}</span>
        </article>
        <article className="cp-catalog-stat">
          <span className="cp-catalog-stat__label">{copy.KPI_SITES}</span>
          <span className="cp-catalog-stat__value">{formatNumber(stock.active_sites)}</span>
        </article>
        <article
          className={`cp-catalog-stat cp-catalog-stat--clickable${
            stockAlerts > 0 ? ' cp-catalog-stat--amber' : ''
          }`}
          role="button"
          tabIndex={0}
          onClick={() => navigate(CENTRAL_PHARMACY_PATHS.ALERTS)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              navigate(CENTRAL_PHARMACY_PATHS.ALERTS);
            }
          }}
        >
          <span className="cp-catalog-stat__label">{copy.KPI_STOCK_ALERTS}</span>
          <span className="cp-catalog-stat__value">{formatNumber(stockAlerts)}</span>
          <span className="cp-catalog-stat__hint">
            {copy.KPI_STOCK_ALERTS_HINT.replace(
              '{n}',
              formatNumber(alertSummary?.out_of_stock || 0)
            )}
          </span>
        </article>
        <article
          className={`cp-catalog-stat cp-catalog-stat--clickable${
            expiryAlerts > 0 ? ' cp-catalog-stat--amber' : ''
          }`}
          role="button"
          tabIndex={0}
          onClick={() => navigate(CENTRAL_PHARMACY_PATHS.ALERTS)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              navigate(CENTRAL_PHARMACY_PATHS.ALERTS);
            }
          }}
        >
          <span className="cp-catalog-stat__label">{copy.KPI_EXPIRY_ALERTS}</span>
          <span className="cp-catalog-stat__value">{formatNumber(expiryAlerts)}</span>
          <span className="cp-catalog-stat__hint">
            {copy.KPI_EXPIRY_ALERTS_HINT.replace(
              '{n}',
              formatNumber(alertSummary?.expired || 0)
            )}
          </span>
        </article>
      </section>

      <div className="cp-dash-grid">
        <section className="cp-card cp-dash-panel">
          <div className="cp-card__head">
            <div>
              <h2 className="cp-card__title">{copy.SECTION_QUEUE}</h2>
              <p className="cp-dash-panel__hint">{copy.SECTION_QUEUE_HINT}</p>
            </div>
            <span className="cp-dash-panel__meta">
              {formatNumber(workflow.dispensations_today)} {copy.KPI_TODAY_DISPENSE.toLowerCase()}
            </span>
          </div>
          <div className="cp-card__body cp-card__body--flush">
            <div className="cp-dash-queue">
              <QueueItem
                label={copy.QUEUE_REQUESTS}
                hint={copy.QUEUE_REQUESTS_HINT.replace(
                  '{n}',
                  formatNumber(workflow.supply_requests_urgent || 0)
                )}
                value={workflow.supply_requests_pending}
                to={CENTRAL_PHARMACY_PATHS.REQUESTS}
                tone={workflow.supply_requests_urgent > 0 ? 'danger' : 'default'}
                icon="list"
              />
              <QueueItem
                label={copy.QUEUE_ORDERS}
                hint={copy.QUEUE_ORDERS_HINT}
                value={workflow.orders_to_process}
                to={CENTRAL_PHARMACY_PATHS.ORDERS}
                icon="truck"
              />
              <QueueItem
                label={copy.QUEUE_RECEIVE}
                hint={copy.QUEUE_RECEIVE_HINT}
                value={workflow.orders_awaiting_reception}
                to={CENTRAL_PHARMACY_PATHS.ORDERS}
                icon="warehouse"
              />
              <QueueItem
                label={copy.QUEUE_DISTRIBUTIONS}
                hint={copy.QUEUE_DISTRIBUTIONS_HINT}
                value={workflow.distributions_in_progress}
                to={CENTRAL_PHARMACY_PATHS.DISTRIBUTIONS}
                icon="exchange-alt"
              />
              <QueueItem
                label={copy.QUEUE_DISPENSATIONS}
                hint={copy.QUEUE_DISPENSATIONS_HINT}
                value={workflow.dispensations_pending}
                to={CENTRAL_PHARMACY_PATHS.DISPENSATIONS}
                icon="pills"
              />
              <QueueItem
                label={copy.QUEUE_TODAY}
                hint={copy.QUEUE_TODAY_HINT}
                value={workflow.dispensations_today}
                to={CENTRAL_PHARMACY_PATHS.DISPENSATIONS}
                tone="success"
                icon="checkCircle"
              />
            </div>
          </div>
        </section>

        <section className="cp-card cp-dash-panel">
          <div className="cp-card__head">
            <div>
              <h2 className="cp-card__title">{copy.SECTION_ALERTS}</h2>
              <p className="cp-dash-panel__hint">{copy.SECTION_ALERTS_HINT}</p>
            </div>
            <button
              type="button"
              className="cp-btn cp-btn--ghost cp-btn--sm"
              onClick={() => navigate(CENTRAL_PHARMACY_PATHS.ALERTS)}
            >
              {copy.SEE_ALL_ALERTS}
            </button>
          </div>
          <div className="cp-card__body">
            {recentAlerts.length === 0 ? (
              <div className="cp-empty-state cp-empty-state--compact">
                <span className="cp-empty-state__icon" aria-hidden>
                  <Icon name="checkCircle" size={24} />
                </span>
                <h3 className="cp-empty-state__title">{copy.EMPTY_ALERTS}</h3>
                <p className="cp-empty-state__text mb-0">{copy.EMPTY_ALERTS_HINT}</p>
              </div>
            ) : (
              <ul className="cp-dash-alert-list">
                {recentAlerts.map((a, idx) => (
                  <li key={`${a.type}-${a.medication_id}-${a.site_id}-${a.lot_id || idx}`}>
                    <button
                      type="button"
                      className="cp-dash-alert-list__item"
                      onClick={() => navigate(CENTRAL_PHARMACY_PATHS.ALERTS)}
                    >
                      <span className={`cp-status-pill ${alertTypePillClass(a.type)}`}>
                        {alertTypeLabel(a.type)}
                      </span>
                      <span className="cp-dash-alert-list__body">
                        <strong>{a.medication_name || a.message}</strong>
                        <span>
                          {a.site_name || '—'}
                          {a.is_central ? ' · centrale' : ''} · {alertDetailText(a)}
                        </span>
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </section>
      </div>

      <section className="cp-dash-quick-section" aria-label={copy.SECTION_QUICK}>
        <h2 className="cp-dash-quick-section__title">{copy.SECTION_QUICK}</h2>
        <div className="cp-dash-quick">
          <Link to={CENTRAL_PHARMACY_PATHS.DISPENSATION_NEW} className="cp-dash-quick__card">
            <span className="cp-dash-quick__icon" aria-hidden>
              <Icon name="pills" size={18} />
            </span>
            <span>
              <span className="cp-dash-quick__title">{copy.QUICK_DISPENSE}</span>
              <span className="cp-dash-quick__hint">{copy.QUICK_DISPENSE_HINT}</span>
            </span>
          </Link>
          <Link to={CENTRAL_PHARMACY_PATHS.REQUESTS} className="cp-dash-quick__card">
            <span className="cp-dash-quick__icon" aria-hidden>
              <Icon name="list" size={18} />
            </span>
            <span>
              <span className="cp-dash-quick__title">{copy.QUICK_REQUESTS}</span>
              <span className="cp-dash-quick__hint">{copy.QUICK_REQUESTS_HINT}</span>
            </span>
          </Link>
          <Link to={CENTRAL_PHARMACY_PATHS.ORDERS} className="cp-dash-quick__card">
            <span className="cp-dash-quick__icon" aria-hidden>
              <Icon name="truck" size={18} />
            </span>
            <span>
              <span className="cp-dash-quick__title">{copy.QUICK_ORDERS}</span>
              <span className="cp-dash-quick__hint">{copy.QUICK_ORDERS_HINT}</span>
            </span>
          </Link>
          <Link to={CENTRAL_PHARMACY_PATHS.ALERTS} className="cp-dash-quick__card">
            <span className="cp-dash-quick__icon" aria-hidden>
              <Icon name="bell" size={18} />
            </span>
            <span>
              <span className="cp-dash-quick__title">{copy.QUICK_ALERTS}</span>
              <span className="cp-dash-quick__hint">{copy.QUICK_ALERTS_HINT}</span>
            </span>
          </Link>
          <Link to={CENTRAL_PHARMACY_PATHS.INVENTORIES} className="cp-dash-quick__card">
            <span className="cp-dash-quick__icon" aria-hidden>
              <Icon name="clipboard-list" size={18} />
            </span>
            <span>
              <span className="cp-dash-quick__title">{copy.QUICK_INVENTORY}</span>
              <span className="cp-dash-quick__hint">{copy.QUICK_INVENTORY_HINT}</span>
            </span>
          </Link>
          <Link to={CENTRAL_PHARMACY_PATHS.MOVEMENTS} className="cp-dash-quick__card">
            <span className="cp-dash-quick__icon" aria-hidden>
              <Icon name="history" size={18} />
            </span>
            <span>
              <span className="cp-dash-quick__title">{copy.QUICK_MOVEMENTS}</span>
              <span className="cp-dash-quick__hint">
                {workflow.movements_today != null
                  ? `${formatNumber(workflow.movements_today)} ${copy.KPI_MOVEMENTS.toLowerCase()}`
                  : copy.QUICK_MOVEMENTS_HINT}
              </span>
            </span>
          </Link>
          <Link to={CENTRAL_PHARMACY_PATHS.REPORTS} className="cp-dash-quick__card">
            <span className="cp-dash-quick__icon" aria-hidden>
              <Icon name="chartBar" size={18} />
            </span>
            <span>
              <span className="cp-dash-quick__title">{copy.QUICK_REPORTS}</span>
              <span className="cp-dash-quick__hint">{copy.QUICK_REPORTS_HINT}</span>
            </span>
          </Link>
        </div>
      </section>
    </div>
  );
}
